
export const RequestStatusValues = {
    NOT_STARTED: 'not_started',
    SUCCESS: 'success',
    FAILED: 'failed',
} as const;

export type RequestStatus = typeof RequestStatusValues[keyof typeof RequestStatusValues];

export type TriggerSource = 'auto' | 'manual';

export type MatchingSourcePpType = 'shopify' | 'authorize_net';

export interface SourceMatchLogMetadata {
    source: { position: number; pp_type: MatchingSourcePpType; account_id: number | null };
    order: { name: string } | null;
    note: string | null;
}

export interface AlertLog {
    log_id: number;
    alert_id: number;
    pp_account_id: number | null;
    pp_type: string | null;
    descriptor_id: number | null;
    descriptor_name: string | null;
    merchant_id: number | null;
    merchant_name: string | null;
    alert_type: string | null;
    alert_source: string | null;
    alert_amount: number | null;
    alert_currency: string | null;
    alert_request: RequestStatus | null;
    alert_resolve_status: string | null;
    alert_resolve_refunded: string | null;
    match_method: MatchMethod | null;
    matched_order_name: string | null;
    matched_order_url: string | null;
    refund_request: RequestStatus | null;
    refund_amount: number | null;
    refund_currency: string | null;
    dry_run: boolean;
    status: RefundLogStatus;
    error_message: string | null;
    trigger_source: TriggerSource;
    metadata: Record<string, any> | null;
    user_email: string | null;
    notes: string | null;
    created_at: Date | string;
}

export type MatchMethod = 'auth_code' | 'auth_code_card_bin_last4' | 'auth_code_via_authorize' | 'auth_code_card_bin_last4_via_authorize' | 'auth_code_card_last4_via_authorize' | 'transaction_id' | 'card_bin_last4' | 'card_bin_last4_via_authorize' | 'manual' | 'adyen_auth_code' | 'adyen_auth_code_card_bin_last4' | 'adyen_card_last4' | 'authorize_net_auth_code' | 'authorize_net_auth_code_card_last4' | 'authorize_net_card_last4' | 'auth_code_transaction_id' | 'auth_code_exact_amount' | 'auth_code_arn_date' | 'auth_code_transaction_id_via_authorize' | 'auth_code_exact_amount_via_authorize' | 'auth_code_arn_date_via_authorize' | 'card_bin_last4_transaction_id' | 'card_bin_last4_exact_amount' | 'card_bin_last4_arn_date' | 'card_bin_last4_transaction_id_via_authorize' | 'card_bin_last4_exact_amount_via_authorize' | 'card_bin_last4_arn_date_via_authorize' | 'network_transaction_id' | 'auth_code_card_last4' | 'card_last4' | 'card_last4_transaction_id' | 'card_last4_exact_amount' | 'card_last4_arn_date';

export const RefundLogStatus = {
    SUCCESS: 'success',
    NO_MATCH: 'no_match',
    MATCH_FAILED: 'match_failed',
    REFUND_FAILED: 'refund_failed',
    RESOLVE_FAILED: 'resolve_failed',
    DRY_RUN: 'dry_run',
    ORDER_DETAILS_FAILED: 'order_details_failed',
    AMBIGUOUS: 'ambiguous',
    ALREADY_REFUNDED: 'already_refunded',
    DUPLICATE_ALERT: 'duplicate_alert',
    ALREADY_REFUNDED_NO_DUPLICATE: 'already_refunded_no_duplicate',
    NOT_FULFILLED: 'not_fulfilled',
    UNEXPECTED_FULFILLMENT_STATUS: 'unexpected_fulfillment_status',
    CANCELLED: 'cancelled',
    CANCEL_FAILED: 'cancel_failed',
    MULTIPLE_TRANSACTIONS: 'multiple_transactions',
    MANUAL_SUCCESS: 'manual_success',
    MANUAL_FAILED: 'manual_failed',
    CM_STATUS_UPDATED: 'cm_status_updated',
    NOTES_EDITED: 'notes_edited',
    SUBSCRIPTION_CANCELLED: 'subscription_cancelled',
    SUBSCRIPTION_NOT_SUBSCRIPTION: 'subscription_not_subscription',
    SUBSCRIPTION_NOT_FOUND: 'subscription_not_found',
    MERCHANT_NO_SUBSCRIPTIONS: 'merchant_no_subscriptions',
    SUBSCRIPTION_STATUS_COPIED: 'subscription_status_copied',
    LOOP_CANCELLED: 'loop_cancelled',
    LOOP_ALREADY_CANCELLED: 'loop_already_cancelled',
    LOOP_NOT_SUBSCRIPTION: 'loop_not_subscription',
    LOOP_FAILED: 'loop_failed',
    KACHING_CANCELLED: 'kaching_cancelled',
    KACHING_ALREADY_CANCELLED: 'kaching_already_cancelled',
    KACHING_NOT_SUBSCRIPTION: 'kaching_not_subscription',
    KACHING_FAILED: 'kaching_failed',
    COC_SUBSCRIPTION_CANCELLED: 'coc_subscription_cancelled',
    COC_SUBSCRIPTION_ALREADY_CANCELLED: 'coc_subscription_already_cancelled',
    COC_NOT_SUBSCRIPTION: 'coc_not_subscription',
    COC_SUBSCRIPTION_FAILED: 'coc_subscription_failed',
    COC_VOID_INSTEAD_OF_REFUND: 'coc_void_instead_of_refund',
    COC_REFUND_UNSETTLED: 'coc_refund_unsettled',
    COC_REFUND_VERIFIED: 'coc_refund_verified',
    COC_PORTAL_REFUND_SUCCESS: 'coc_portal_refund_success',
    COC_PORTAL_REFUND_FAILED: 'coc_portal_refund_failed',
    COC_PORTAL_REFUND_DRY_RUN: 'coc_portal_refund_dry_run',
    COC_REFUND_PENDING: 'coc_refund_pending',
    CRM_REFUND_PENDING: 'crm_refund_pending',
    CRM_REFUND_VERIFIED: 'crm_refund_verified',
    CRM_REFUND_UNSETTLED: 'crm_refund_unsettled',
    CRM_SUBSCRIPTION_CANCELLED: 'crm_subscription_cancelled',
    CRM_SUBSCRIPTION_ALREADY_CANCELLED: 'crm_subscription_already_cancelled',
    CRM_NOT_SUBSCRIPTION: 'crm_not_subscription',
    CRM_SUBSCRIPTION_FAILED: 'crm_subscription_failed',
    CRM_ORDER_CANCELLED: 'crm_order_cancelled',
    CRM_ORDER_ALREADY_CANCELLED: 'crm_order_already_cancelled',
    CRM_ORDER_NOT_CANCELLABLE: 'crm_order_not_cancellable',
    CRM_ORDER_CANCEL_FAILED: 'crm_order_cancel_failed',
    CRM_EXTERNAL_REFUND_RECORDED: 'crm_external_refund_recorded',
    CRM_EXTERNAL_REFUND_ALREADY_REFUNDED: 'crm_external_refund_already_refunded',
    CRM_EXTERNAL_REFUND_FAILED: 'crm_external_refund_failed',
    SHOPIFY_REFUND_VERIFIED: 'shopify_refund_verified',
    SHOPIFY_REFUND_UNSETTLED: 'shopify_refund_unsettled',
    SHOPIFY_REFUND_PENDING: 'shopify_refund_pending',
    COC_EXTERNAL_REFUND_RECORDED: 'coc_external_refund_recorded',
    COC_EXTERNAL_REFUND_ALREADY_REFUNDED: 'coc_external_refund_already_refunded',
    COC_EXTERNAL_REFUND_FAILED: 'coc_external_refund_failed',
    COC_VOIDED: 'coc_voided',
    COC_VOID_FAILED: 'coc_void_failed',
    COC_FULFILLMENT_CANCELLED: 'coc_fulfillment_cancelled',
    COC_FULFILLMENT_ALREADY_CANCELLED: 'coc_fulfillment_already_cancelled',
    COC_FULFILLMENT_NOT_CANCELLABLE: 'coc_fulfillment_not_cancellable',
    COC_FULFILLMENT_OTHER_ITEMS: 'coc_fulfillment_other_items',
    COC_FULFILLMENT_NOT_FOUND: 'coc_fulfillment_not_found',
    COC_FULFILLMENT_FAILED: 'coc_fulfillment_failed',
    COC_SHOPIFY_ORDER_CANCELLED: 'coc_shopify_order_cancelled',
    COC_SHOPIFY_ORDER_ALREADY_CANCELLED: 'coc_shopify_order_already_cancelled',
    COC_SHOPIFY_ORDER_NOT_CANCELLABLE: 'coc_shopify_order_not_cancellable',
    COC_SHOPIFY_ORDER_CANCEL_FAILED: 'coc_shopify_order_cancel_failed',
    COC_SHOPIFY_ORDER_NOT_FOUND: 'coc_shopify_order_not_found',
    SHOPIFY_ORDER_CANCELLED: 'shopify_order_cancelled',
    SHOPIFY_ORDER_ALREADY_CANCELLED: 'shopify_order_already_cancelled',
    SHOPIFY_ORDER_NOT_CANCELLABLE: 'shopify_order_not_cancellable',
    SHOPIFY_ORDER_CANCEL_FAILED: 'shopify_order_cancel_failed',
    REFUNDED_EXTERNALLY_UPDATED: 'refunded_externally_updated',
    FULFILLMENT_STATUS_UPDATED: 'fulfillment_status_updated',
    ETHOCA_VISA_SKIPPED: 'ethoca_visa_skipped',
    MAX_AMOUNT_EXCEEDED: 'max_amount_exceeded',
    ADYEN_MATCH_FOUND: 'adyen_match_found',
    ADYEN_MATCH_FOUND_DRY_RUN: 'adyen_match_found_dry_run',
    ADYEN_MATCH_NOT_IN_COC: 'adyen_match_not_in_coc',
    COC_MATCHED_ON_OTHER_ACCOUNT: 'coc_matched_on_other_account',
    SOURCE_MATCH_FOUND: 'source_match_found',
    SOURCE_MATCH_AMBIGUOUS: 'source_match_ambiguous',
    SOURCE_NO_MATCH: 'source_no_match',
    PREMATCHED: 'prematched',
    PREMATCH_NO_MATCH: 'prematch_no_match',
    PREMATCH_AMBIGUOUS: 'prematch_ambiguous',
} as const;

export type RefundLogStatus = typeof RefundLogStatus[keyof typeof RefundLogStatus];
